import {
  type DimensionOption,
  type IntegrationMeta,
  labelFor,
  PROVIDER_SUPPORTED_BREAKDOWNS,
} from '@alisonai/alx-sdk';
import { groupByPlatform, toAccountRows } from '../filters/leadingKpi';
import { type FilterKey, humanize } from '../filters/registry';
import type { PanelValues } from '../filters/types';
import { fallbackValues } from './preflightFallback';

export const PREFLIGHT = 'preflight';
export const RECALC_PREFLIGHT = 'recalcPreflightCreativeScores';

export const CAMPAIGN_DIMENSIONS = [
  'campaignObjective',
  'optimizationGoal',
  'platformBreakdown',
  'country',
] as const satisfies readonly FilterKey[];

export type CampaignDimension = (typeof CAMPAIGN_DIMENSIONS)[number];

const METADATA_FIELD: Record<CampaignDimension, string> = {
  campaignObjective: 'campaignObjective',
  optimizationGoal: 'optimizationTarget',
  platformBreakdown: 'platform',
  country: 'country',
};

const BREAKDOWN_AXIS: readonly CampaignDimension[] = ['platformBreakdown', 'country'];

const DIMENSION_LABEL: Record<CampaignDimension, string> = {
  campaignObjective: 'Campaign Objective',
  optimizationGoal: 'Optimization Target',
  platformBreakdown: 'Placement',
  country: 'Country',
};

export interface Platform {
  slug: string;
  label: string;
  networkId: number;
}

export interface PlatformFieldOption {
  id: string;
  label: string;
}

export interface PlatformField {
  key: string;
  label: string;
  kind: 'text' | 'select';
  blocked: boolean;
  options: PlatformFieldOption[];
  unavailable: boolean;
}

export function accountsByPlatform(accountMetas: IntegrationMeta[]): Map<string, string[]> {
  return groupByPlatform(toAccountRows(accountMetas));
}

export interface OfferedNetwork {
  slug: string;
  display_name: string;
  network_id: number;
}

export function offeredPlatforms(
  networks: readonly OfferedNetwork[],
  labels: Record<string, string>,
): Platform[] {
  return networks.map((network) => ({
    slug: network.slug,
    label: labels[network.slug] ?? network.display_name,
    networkId: network.network_id,
  }));
}

const CAMPAIGN_NAME_FIELD: PlatformField = {
  key: 'campaignName',
  label: 'Campaign Name',
  kind: 'text',
  blocked: false,
  options: [],
  unavailable: false,
};

function labelOfValue(dimension: CampaignDimension, value: string, curated?: string): string {
  if (curated !== undefined) return curated;
  if (dimension === 'country') return labelFor('country', value) ?? value;
  return humanize(value);
}

/** Whether any account in the scope reports a country, from the SDK's per-scope coverage. */
export function reportsCountry(dimensions: ReadonlyArray<DimensionOption>): boolean {
  const country = dimensions.find((dimension) => dimension.name === 'country');
  return (country?.coverage?.n_covered ?? 0) > 0;
}

/** With no account in scope to ask, whether the network's default report carries a country. An
 * opt-in country (Google's) shows only in a scope's coverage. A network the SDK does not list keeps it. */
function reportsCountryByDefault(platform: string): boolean {
  if (!Object.hasOwn(PROVIDER_SUPPORTED_BREAKDOWNS, platform)) return true;
  return PROVIDER_SUPPORTED_BREAKDOWNS[platform]?.has('country') ?? true;
}

function dimensionsFor(reportsCountry: boolean): readonly CampaignDimension[] {
  return reportsCountry
    ? CAMPAIGN_DIMENSIONS
    : CAMPAIGN_DIMENSIONS.filter((dimension) => dimension !== 'country');
}

function blockedByPicks(selected: Record<string, string>): Set<CampaignDimension> {
  const axis = BREAKDOWN_AXIS.find((dimension) => Boolean(selected[METADATA_FIELD[dimension]]));
  return new Set(
    axis === undefined ? [] : BREAKDOWN_AXIS.filter((dimension) => dimension !== axis),
  );
}

function selectField(
  dimension: CampaignDimension,
  options: PlatformFieldOption[],
  blocked: boolean,
): PlatformField {
  return {
    key: METADATA_FIELD[dimension],
    label: DIMENSION_LABEL[dimension],
    kind: 'select',
    blocked,
    options: blocked ? [] : options,
    unavailable: blocked || options.length === 0,
  };
}

function backfilled(
  platform: string,
  dimension: CampaignDimension,
  offered: PlatformFieldOption[],
): PlatformFieldOption[] {
  if (offered.length > 0) return offered;
  return fallbackValues(platform, dimension).map((value) => ({
    id: value,
    label: labelOfValue(dimension, value),
  }));
}

export function fallbackPlatformFields(
  platform: string,
  selected: Record<string, string> = {},
): PlatformField[] {
  const blocked = blockedByPicks(selected);
  return [
    CAMPAIGN_NAME_FIELD,
    ...dimensionsFor(reportsCountryByDefault(platform)).map((dimension) =>
      selectField(dimension, backfilled(platform, dimension, []), blocked.has(dimension)),
    ),
  ];
}

/** `reportsCountry`: whether any of the scope's accounts on this platform report a country, as the
 * SDK's dimension coverage says. */
export function toPlatformFields(
  platform: string,
  values: PanelValues,
  reportsCountry: boolean,
): PlatformField[] {
  const scored = dimensionsFor(reportsCountry).map((dimension) => {
    const bucket = values[dimension];
    const options = (bucket?.values ?? []).map((value) => ({
      id: value,
      label: labelOfValue(dimension, value, bucket?.labels?.[value]),
    }));
    return selectField(
      dimension,
      backfilled(platform, dimension, options),
      bucket?.unavailable === true,
    );
  });
  return [CAMPAIGN_NAME_FIELD, ...scored];
}

export const CASCADING_FIELDS: readonly string[] = CAMPAIGN_DIMENSIONS.map(
  (dimension) => METADATA_FIELD[dimension],
);

export function cascadingPicks(entered: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(entered).filter(([field]) => CASCADING_FIELDS.includes(field)),
  );
}

export function pickedDimensions(
  selected: Record<string, string>,
): Partial<Record<CampaignDimension, string>> {
  const blocked = blockedByPicks(selected);
  return Object.fromEntries(
    CAMPAIGN_DIMENSIONS.flatMap((dimension) => {
      const value = selected[METADATA_FIELD[dimension]];
      return value === undefined || value === '' || blocked.has(dimension)
        ? []
        : [[dimension, value] as const];
    }),
  );
}

export function dimensionOf(metadataField: string): CampaignDimension | null {
  return (
    CAMPAIGN_DIMENSIONS.find((dimension) => METADATA_FIELD[dimension] === metadataField) ?? null
  );
}
